import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { VISA_ORDER } from "@/data/quiz";
import { API, EMAIL_RE } from "@/config";
import {
  MoonStar,
  Globe2,
  Languages,
  MapPin,
  Wallet,
  Percent,
  HeartPulse,
  GraduationCap,
  Plane,
  Users,
  ArrowRight,
  Clock,
  CheckCircle2,
  Loader2,
  Mail,
  ListChecks,
  Calculator,
  Sparkles,
  HelpCircle,
} from "lucide-react";



const HERO_IMAGE = "/assets/hero.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const BENEFIT_ICONS = {
  muslim: MoonStar,
  open: Globe2,
  english: Languages,
  singapore: MapPin,
  cost: Wallet,
  tax: Percent,
  health: HeartPulse,
  education: GraduationCap,
  travel: Plane,
  community: Users,
};

const BENEFIT_ORDER = [
  "muslim",
  "open",
  "english",
  "singapore",
  "cost",
  "tax",
  "health",
  "education",
  "travel",
  "community",
];



const FaqSection = () => {
  const { t } = useLang();
  return (
    <section id="faq" className="py-14 md:py-20 bg-secondary noise-overlay">
      <div className="container-page relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-10 md:mb-14 max-w-2xl"
        >
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="h-6 w-6 text-accent" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-[-0.02em]">
              {t.faq.heading}
            </h2>
          </div>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            {t.faq.sub}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {t.faq.items.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="rounded-[var(--radius)] bg-card border border-border shadow-[0_1px_0_rgba(36,27,20,0.06)] px-5 border-b"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left font-heading font-semibold text-sm md:text-base tracking-[-0.01em] hover:no-underline py-4 gap-4"
                >
                  <span className="flex items-center gap-3">
                    <Badge
                      variant="secondary"
                      className="bg-secondary border border-border rounded-lg text-xs font-normal text-accent shrink-0 hidden sm:inline-flex"
                    >
                      {item.category}
                    </Badge>
                    {item.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

const NewsletterSection = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      toast.error(t.newsletter.invalid);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        email: email.trim(),
        source: "newsletter",
        language: lang,
      });
      if (res.data.duplicate) {
        toast.success(t.newsletter.duplicate);
      } else {
        toast.success(t.newsletter.success);
      }
      setEmail("");
    } catch (err) {
      toast.error(t.newsletter.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="newsletter" className="py-14 md:py-20">
      <div className="container-page">
        <Card className="bg-card border border-border rounded-[var(--radius)] shadow-[0_1px_0_rgba(36,27,20,0.06)] overflow-hidden">
          <CardContent className="p-8 md:p-12 section-teal-hint">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="h-5 w-5 text-accent" />
                <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-[-0.02em]">
                  {t.newsletter.heading}
                </h2>
              </div>
              <p className="text-muted-foreground mb-6 leading-relaxed">{t.newsletter.sub}</p>
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
                <Input
                  data-testid="newsletter-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.newsletter.placeholder}
                  className="h-12 rounded-xl bg-background flex-1"
                  aria-label={t.newsletter.placeholder}
                />
                <Button
                  data-testid="newsletter-submit-button"
                  type="submit"
                  disabled={loading}
                  className="h-12 px-6 rounded-xl active:scale-[0.98] transition-transform duration-150"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t.newsletter.button}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-3">{t.newsletter.consent}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default function Landing() {
  const { t } = useLang();

  const stats = [
    { value: t.hero.statCost, label: t.hero.statCostLabel },
    { value: t.hero.statFlight, label: t.hero.statFlightLabel },
    { value: t.hero.statFrench, label: t.hero.statFrenchLabel },
    { value: t.hero.statHalal, label: t.hero.statHalalLabel },
  ];

  const stepIcons = [ListChecks, Calculator, Mail];

  return (
    <div className="min-h-screen bg-background">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-20 focus:left-4 focus:z-[60] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-xl focus:text-sm focus:font-medium">
        {t.nav.benefits}
      </a>
      <Navbar showAnchors />

      <main id="main-content">
      {/* ============ HERO ============ */}
      <section className="hero-warm-wash noise-overlay">
        <div className="container-page py-14 md:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} className="relative z-10">
              <Badge
                variant="secondary"
                className="mb-5 bg-secondary text-secondary-foreground border border-border rounded-lg px-3 py-1"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5 text-primary" />
                {t.hero.badge}
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-tight mb-5">
                {t.hero.title}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <Button
                  asChild
                  data-testid="hero-start-quiz-button"
                  className="h-12 px-6 rounded-xl text-base active:scale-[0.98] transition-transform duration-150"
                >
                  <Link to="/quiz">
                    {t.hero.ctaPrimary}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="hero-see-visas-button"
                  className="h-12 px-6 rounded-xl text-base border-border bg-card hover:bg-secondary active:scale-[0.98] transition-transform duration-150"
                >
                  <a href="#visas">{t.hero.ctaSecondary}</a>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4 text-accent" />
                {t.hero.trust}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: "easeOut", delay: 0.15 }}
              className="relative z-10"
            >
              <img
                src={HERO_IMAGE}
                alt={t.hero.imageAlt}
                className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-[0_20px_50px_rgba(36,27,20,0.18)]"
                loading="eager"
                width={800}
                height={320}
              />
              <div className="grid grid-cols-2 gap-3 mt-4">
                {stats.map((s, i) => (
                  <div
                    key={i}
                    className="rounded-[var(--radius)] bg-card border border-border p-4 shadow-[0_1px_0_rgba(36,27,20,0.06)]"
                  >
                    <p className="font-mono-ui text-2xl font-semibold text-primary">{s.value}</p>
                    <p className="text-xs md:text-sm text-muted-foreground leading-snug mt-1">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section id="benefits" className="py-14 md:py-20">
        <div className="container-page">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-10 md:mb-14 max-w-2xl"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-3">
              {t.benefits.heading}
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              {t.benefits.sub}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFIT_ORDER.map((key, i) => {
              const Icon = BENEFIT_ICONS[key];
              const item = t.benefits.items[key];
              const featured = key === "singapore";
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: (i % 3) * 0.07 }}
                  className={featured ? "md:col-span-2" : ""}
                >
                  <Card
                    data-testid={`benefit-card-${key}`}
                    className={`h-full rounded-[var(--radius)] border border-border shadow-[0_1px_0_rgba(36,27,20,0.06)] hover:shadow-[0_10px_30px_rgba(36,27,20,0.10)] hover:-translate-y-0.5 transition-shadow transition-transform duration-200 ${
                      featured ? "bg-card section-teal-hint" : "bg-card"
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary">
                          <Icon className="h-6 w-6" />
                        </span>
                        {featured && item.badge && (
                          <Badge className="bg-accent text-accent-foreground rounded-lg">
                            {item.badge}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-heading font-semibold text-lg tracking-[-0.01em] mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ VISAS ============ */}
      <section id="visas" className="py-14 md:py-20 bg-secondary noise-overlay">
        <div className="container-page relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-10 md:mb-14 max-w-2xl"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-3">
              {t.visasSection.heading}
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              {t.visasSection.sub}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {VISA_ORDER.map((visaId, i) => {
              const visa = t.visas[visaId];
              return (
                <motion.div
                  key={visaId}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: (i % 3) * 0.07 }}
                >
                  <Card
                    data-testid={`visa-card-${visaId}`}
                    className="h-full flex flex-col rounded-[var(--radius)] bg-card border border-border shadow-[0_1px_0_rgba(36,27,20,0.06)] hover:shadow-[0_10px_30px_rgba(36,27,20,0.10)] hover:-translate-y-0.5 transition-shadow transition-transform duration-200"
                  >
                    <CardContent className="p-6 flex flex-col flex-1">
                      <Badge
                        variant="secondary"
                        className="self-start mb-3 bg-secondary border border-border rounded-lg font-mono-ui text-xs"
                      >
                        {visa.name}
                      </Badge>
                      <h3 className="font-heading font-semibold text-lg tracking-[-0.01em] mb-1">
                        {visa.fullName}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {visa.tagline}
                      </p>
                      <div className="space-y-2.5 text-sm mb-5">
                        <div className="flex items-start gap-2">
                          <Users className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                          <span>
                            <span className="font-medium">{t.visasSection.forWho} : </span>
                            <span className="text-muted-foreground">{visa.forWho}</span>
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                          <span>
                            <span className="font-medium">{t.visasSection.duration} : </span>
                            <span className="text-muted-foreground">{visa.duration}</span>
                          </span>
                        </div>
                      </div>
                      <div className="mt-auto">
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          data-testid={`visa-card-cta-${visaId}`}
                          className="rounded-xl border-border bg-card hover:bg-secondary w-full active:scale-[0.98] transition-transform duration-150"
                        >
                          <Link to="/quiz">
                            {t.visasSection.cta}
                            <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how-it-works" className="py-14 md:py-20">
        <div className="container-page">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="font-heading text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-10 md:mb-14"
          >
            {t.howItWorks.heading}
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {t.howItWorks.steps.map((step, i) => {
              const Icon = stepIcons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: i * 0.1 }}
                >
                  <Card className="h-full rounded-[var(--radius)] bg-card border border-border shadow-[0_1px_0_rgba(36,27,20,0.06)]">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground font-mono-ui font-semibold">
                          {i + 1}
                        </span>
                        <Icon className="h-5 w-5 text-accent" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg tracking-[-0.01em] mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                        {step.desc}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center">
            <Button
              asChild
              data-testid="how-it-works-cta-button"
              className="h-12 px-8 rounded-xl text-base active:scale-[0.98] transition-transform duration-150"
            >
              <Link to="/quiz">
                {t.howItWorks.cta}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <FaqSection />
      <NewsletterSection />
      </main>
      <Footer />
    </div>
  );
}
